import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { employeeTimeAllocationApi } from '../../services/employeeTimeAllocation';
import { useAuth } from '../../context/AuthContext';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/Pagination';

function TimeAllocationList() {
  const { hasRole } = useAuth();
  const [timeAllocationData, setTimeAllocationData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { pagination, handlePageChange, updatePagination } = usePagination();

  useEffect(() => {
    fetchTimeAllocation();
  }, [pagination.page]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = timeAllocationData.filter(timeAllocation =>
        timeAllocation.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        timeAllocation.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(timeAllocationData);
    }
  }, [searchQuery, timeAllocationData]);

  const handleClear = useCallback(() => {
    setSearchQuery('');
  }, []);

  const isFilterActive = searchQuery;

  const fetchTimeAllocation = async () => {
    try {
      setLoading(true);
      const response = await employeeTimeAllocationApi.getAll({ page: pagination.page, limit: pagination.limit });
      const data = Array.isArray(response) ? response : (response.data || []);
      setTimeAllocationData(data);
      if (response.pagination) {
        updatePagination(response.pagination);
      }
    } catch (err) {
      console.error('Failed to load time allocation data:', err);
      setTimeAllocationData([]);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAllocationBadge = (percentage) => {
    if (!percentage) return { color: '#9e9e9e', label: 'No data' };
    const colors = {
      '0-25%': '#f44336',
      '25-50%': '#ff9800',
      '50-75%': '#2196f3',
      '75-100%': '#4caf50'
    };
    return { color: colors[percentage] || '#9e9e9e', label: percentage };
  };

  const truncateText = (text, max = 100) => {
    if (!text) return '';
    return text.length > max ? text.substring(0, max) + '...' : text;
  };

  const formatWeekRange = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDay();
    const monday = new Date(date);
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    monday.setDate(diff);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    return `${monday.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${sunday.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  };

  const exportToCSV = () => {
    const headers = ['Employee Name', 'Email', 'Department', 'Role', 'Week', 'Time Allocation', 'Notes', 'Date Added'];
    const rows = timeAllocationData.map(o => [
      o.employee_name,
      o.email,
      o.department,
      o.employee_role,
      formatWeekRange(o.week_start_date),
      o.time_allocation_percentage,
      o.notes || '',
      new Date(o.created_at).toLocaleDateString()
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time_allocation_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Time Allocation Management</h2>
        {filteredData.length > 0 && (
          <button className="btn btn-primary btn-compact" onClick={exportToCSV}>
            Export CSV
          </button>
        )}
      </div>

      <div className="card">
        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
          <input
            type="text"
            placeholder="Search by employee name or email..."
            className="form-control form-control-compact"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ maxWidth: '400px' }}
          />
          {isFilterActive && (
            <button className="btn btn-secondary btn-compact" onClick={handleClear}>
              Clear
            </button>
          )}
        </div>
        {filteredData.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.5)', padding: '20px 0' }}>
            {searchQuery ? 'No matching time allocation data found.' : 'No time allocation data recorded yet.'}
          </p>
        ) : (
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Week</th>
                  <th>Time Allocation</th>
                  <th>Notes</th>
                  <th>Date Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(timeAllocation => {
                  const badge = getTimeAllocationBadge(timeAllocation.time_allocation_percentage);
                  return (
                    <tr key={timeAllocation.id}>
                      <td>
                        <Link 
                          to={`/employees/${timeAllocation.employee_id}`} 
                          style={{ textDecoration: 'none', color: '#1976d2' }}
                        >
                          {timeAllocation.employee_name}
                        </Link>
                      </td>
                      <td>{formatWeekRange(timeAllocation.week_start_date)}</td>
                      <td>
                        <span className="badge" style={{ backgroundColor: badge.color, color: '#fff' }}>
                          {badge.label}
                        </span>
                      </td>
                      <td>{truncateText(timeAllocation.notes) || '-'}</td>
                      <td>{new Date(timeAllocation.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination pagination={pagination} onPageChange={handlePageChange} />
      </div>
    </div>
  );
}

export default TimeAllocationList;
